import { useMemo, useState } from 'react';

import {
  type ErrorLike,
  type InternalRefetchQueriesInclude,
} from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, IconButton, Typography } from '@mui/material';
import { Check, Trash2, X } from 'lucide-react';

import { type PropertyFileFragment } from '@graphql/__generated__/graphql';
import { FileDeleteDialog } from '@modules/_shared/components/FileDeleteDialog';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SortableTable } from '@modules/_shared/components/sortable-list/SortableTable';
import type {
  ReorderEvent,
  SortableTableColDef,
} from '@modules/_shared/components/sortable-list/types';
import { REORDER_PROPERTY_FILES } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

type FileItem = PropertyFileFragment & { id: string };

type FilesSortableListProps = {
  propertyId?: string;
  files: PropertyFileFragment[];
  loading?: boolean;
  readOnly?: boolean;
  fileKind: 'videos' | 'documents';
  refetchQueries?: InternalRefetchQueriesInclude;
  onDelete?: (id: string) => void;
  onSelectFile: (fileId: string) => void;
  selectedFileId?: string | null;
};

export const FilesSortableList = ({
  propertyId,
  files,
  loading = false,
  readOnly = false,
  fileKind,
  refetchQueries,
  onDelete,
  onSelectFile,
  selectedFileId,
}: FilesSortableListProps) => {
  const { t } = useLocale();
  const [deleteFile, setDeleteFile] = useState<PropertyFileFragment | null>(
    null,
  );
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);

  const [reOrderPropertyFiles] = useMutation(REORDER_PROPERTY_FILES, {
    refetchQueries,
    onError: setMutationError,
  });

  const items = useMemo<FileItem[]>(
    () => files.map(file => ({ ...file, id: file.file.id })),
    [files],
  );

  const handleReorder = ({ movedItem, toIndex }: ReorderEvent<FileItem>) => {
    if (propertyId == null) {
      return;
    }
    reOrderPropertyFiles({
      variables: {
        property_id: propertyId,
        file_id: movedItem.file.id,
        target_position: items[toIndex].order_nr,
      },
    });
  };

  const columns = useMemo<SortableTableColDef<FileItem>[]>(() => {
    const cols: SortableTableColDef<FileItem>[] = [
      {
        field: 'name',
        headerName: t('Name'),
        flex: 1,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <FileIcon
              mimetype={row.file.mimetype}
              filename={row.file.name ?? undefined}
            />
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {row.file.name}
            </Typography>
          </Box>
        ),
      },
    ];

    if (fileKind === 'documents') {
      cols.push({
        field: 'is_visible',
        headerName: t('Brochure'),
        width: 96,
        align: 'center',
        renderCell: ({ row }) =>
          row.is_visible ? (
            <Box
              component="span"
              sx={{ display: 'inline-flex', color: 'text.secondary' }}
            >
              <Check size={20} />
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                color: 'text.secondary',
                opacity: 0.5,
              }}
            >
              <X size={20} />
            </Box>
          ),
      });
    }

    cols.push({
      field: 'display_on_listing',
      headerName: t('Portal'),
      width: 96,
      align: 'center',
      renderCell: ({ row }) =>
        row.display_on_listing ? (
          <Box
            component="span"
            sx={{ display: 'inline-flex', color: 'text.secondary' }}
          >
            <Check size={20} />
          </Box>
        ) : (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              color: 'text.secondary',
              opacity: 0.5,
            }}
          >
            <X size={20} />
          </Box>
        ),
    });

    if (!readOnly) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 40,
        align: 'center',
        renderCell: ({ row }) => (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setDeleteFile(row);
            }}
          >
            <Trash2 size={18} />
          </IconButton>
        ),
      });
    }

    return cols;
  }, [t, readOnly, fileKind]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        height: '100%',
      }}
    >
      <FileDeleteDialog
        open={deleteFile != null}
        onDelete={onDelete}
        fileId={deleteFile?.file.id ?? ''}
        onClose={() => setDeleteFile(null)}
      />
      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
      <SortableTable
        items={items}
        columns={columns}
        onReorder={readOnly ? undefined : handleReorder}
        enableDragAndDrop={!readOnly}
        loading={loading}
        onRowClick={item => onSelectFile(item.file.id)}
        selectedItemId={selectedFileId ?? null}
        showHeader
      />
    </Box>
  );
};
